import { ApolloLink } from '@apollo/client';
import {
  CombinedGraphQLErrors,
  ServerError,
  ServerParseError,
} from '@apollo/client/errors';
import { ErrorLink } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { datadogRum } from '@datadog/browser-rum';
import { PrinceSdkProvider } from '@teamexos/prince-sdk';
import fastRedact from 'fast-redact';
import React from 'react';
import { map } from 'rxjs';
import { clientOptions } from '../apollo/client';
import { emitNetworkError, emitNetworkSuccess } from './NetworkErrorGate';

// Coach Hub currently does not have build numbers like the apps but we need to send
// up something until we roll out a forced app upgrade for coach hub
const buildNumber = '1';

const redactObjects = {
  paths: [
    '*.email',
    '*.photo',
    '*.avatar',
    '*.cronofyElementToken',
    '*.firstName',
    '*.lastName',
    '*.name',
    '*.userName',
    '*.messages',
    '*.text',
    '*.photos',
    '*.note',
    '*.signedUrl',
    '*.birthday',
    '*.pronouns',
    '*.age',
    '*.profileJson',
    '*.token',
    '*.iterableToken',
    '*.additionalInfo',
    '*.senderName',
    '*.coachFirstName',
    '*.coachAssignmentFirstName',
    '*.coachAssignmentLastName',
    '*.content',
    '*.planItems[*].description',
    'email',
    'photo',
    'avatar',
    'cronofyElementToken',
    'firstName',
    'lastName',
    'name',
    'userName',
    'messages',
    'text',
    'photos',
    'note',
    'signedUrl',
    'birthday',
    'pronouns',
    'age',
    'profileJson',
    'token',
    'iterableToken',
    'additionalInfo',
    'senderName',
    'coachFirstName',
    'coachAssignmentFirstName',
    'coachAssignmentLastName',
    'content',
  ],
};

const redact = fastRedact(redactObjects);

export const errorLink = new ErrorLink(({ error, operation }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach((graphQLError) => {
      datadogRum.addError(new Error(graphQLError.message), {
        operationName: operation.operationName,
        releaseSHA: process.env.GATSBY_COMMIT_ID,
        variables: redact(operation.variables),
        extensions: operation.extensions,
        apolloGraphQLError: {
          path: graphQLError.path,
          message: graphQLError.message,
          extensions: graphQLError.extensions,
        },
      });
    });
    return;
  }

  // Network-level errors
  if (error.stack?.includes('hammerhead.js')) {
    // filter testcafe e2e noise
    return;
  }
  datadogRum.addError(new Error(error.message), {
    operationName: operation.operationName,
    releaseSHA: process.env.GATSBY_COMMIT_ID,
    variables: redact(operation.variables),
    extensions: operation.extensions,
  });
  // Emit network error event for tracking consecutive failures
  const statusCode =
    ServerError.is(error) || ServerParseError.is(error)
      ? error.statusCode
      : undefined;
  if (statusCode === undefined || statusCode > 500) {
    // Check for cases where API is unreachable (no status code present)
    // or the status code is greater than 500 (indicating a server error)
    // but not 500 itself because that's probably sent by us
    emitNetworkError();
  }
});

const PrinceSDKProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const headersLink = new ApolloLink((operation, forward) => {
    operation.setContext({
      headers: {
        'x-application-version': `web/${buildNumber}/${process.env.GATSBY_COMMIT_ID}`,
        'x-application-name': 'prince-web',
        'x-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });
    return forward(operation);
  });

  // Link to track successful network requests
  const successLink = new ApolloLink((operation, forward) => {
    return forward(operation).pipe(
      map((response) => {
        // Only emit success if there are no GraphQL errors
        // Network errors are handled separately by errorLink and won't reach here
        if (!('errors' in response) || !response.errors?.length) {
          emitNetworkSuccess();
        }
        return response;
      }),
    );
  });

  const retryLink = new RetryLink();

  const link = ApolloLink.from([
    headersLink,
    successLink,
    errorLink,
    retryLink,
  ]);

  return (
    <PrinceSdkProvider
      clientOptions={{
        ...clientOptions,
        extraLink: link,
      }}
    >
      {children}
    </PrinceSdkProvider>
  );
};

export default PrinceSDKProvider;
